import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { minimumCompanySizeForSales, pricingStudioUrl, pricingUrl } from '@/environment';
import flashLightning from '@/images/FlashLightning.svg';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { isEnterprisePlan, IUserSettingsData, PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { ButtonBase } from '@mui/material';

const UpgradeButtonBase = styled(ButtonBase)`
    display: flex;
    padding: 6px 8px;
    gap: 6px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
`;

const UpgradeButton = styled(UpgradeButtonBase)`
    color: ${({ theme }) => theme.colors.primary.main};
    border: 1px solid ${({ theme }) => theme.colors.primary.main};
    background-color: #fff5ed;

    :hover {
        background-color: #ffedde;
    }
`;

const TalkToSalesButton = styled(UpgradeButtonBase)`
    color: ${({ theme }) => theme.colors.secondary.main};
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    background-color: white;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const getUserMinimalCompanySize = (userSettings: IUserSettingsData) => {
    const companySizeFromOnBoardingString: string = userSettings?.features?.onBoarding?.answers?.state?.onboard_new_company_size;
    const companySizeNoTextString = companySizeFromOnBoardingString?.replace(' employees', '');
    // we do it because the text may be A-B or A+ and we want just the first number:
    const sign = companySizeNoTextString?.includes('-') ? '-' : '+';
    const minimalSize = companySizeNoTextString?.substring(0, companySizeNoTextString.indexOf(sign));

    return parseInt(minimalSize);
};

export const CreditsSideBarIndicator = () => {
    const { user, isAnonymous } = useAuth();
    const mixpanel = useAnalytics();
    const { userSettings } = useUserSettings();
    const planGroup = user?.planGroup;

    if (isAnonymous) {
        return null;
    }

    const handleUpgrade = () => {
        mixpanel?.track('upgrade-package', { event: 'click', from: 'sidebar-credits-indicator', plan: planGroup });
        if ([PlanGroup.BUILD, PlanGroup.LAUNCH, PlanGroup.SCALE].includes(planGroup!)) {
            window.open(pricingUrl, '_blank');
        } else {
            window.open(pricingStudioUrl, '_blank');
        }
    };

    const userPlanSupported = planGroup !== PlanGroup.LITE && !isEnterprisePlan(planGroup);
    const companySize = getUserMinimalCompanySize(userSettings) > minimumCompanySizeForSales;
    const isSalesButton = userPlanSupported && companySize;

    const isUpgradeBanner =
        [
            PlanGroup.BUILD,
            PlanGroup.LAUNCH,
            PlanGroup.SCALE,
            PlanGroup.LITE,
            PlanGroup.PRO,
            PlanGroup.ADVANCED,
            PlanGroup.TRIAL,
            PlanGroup.FREE,
            PlanGroup.CREATOR,
            PlanGroup.BUSINESS,
        ].includes(planGroup || ('' as PlanGroup)) &&
        !(planGroup === PlanGroup.SCALE && user?.priceCredits === '1200') &&
        !(planGroup === PlanGroup.ADVANCED && user?.priceCredits === '700');

    return (
        <div>
            {(isUpgradeBanner || isSalesButton) && (
                <ButtonContainer>
                    <UpgradeButton id="chameleon-upgrade-from-side-bar-indicator" onClick={handleUpgrade}>
                        <MaskedIcon src={flashLightning} size="20px" />
                        Upgrade
                    </UpgradeButton>
                    {isSalesButton && (
                        <TalkToSalesButton id="talk-to-sales" onClick={() => mixpanel?.track('contact', { event: 'click', from: 'sales' })}>
                            Talk to Sales
                        </TalkToSalesButton>
                    )}
                </ButtonContainer>
            )}
        </div>
    );
};
